import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UserCircle, LogOut, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Account dropdown (signed-in only) — shows current email, role, and a quick
 * Admin link for admins. Closes on outside click. tone="ivory" for dark
 * backgrounds (homepage hero), tone="charcoal" otherwise.
 */
export default function UserMenu({ tone = "ivory" }) {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center justify-center w-9 h-9 transition-colors ${
          tone === "ivory" ? "text-ivory" : "text-charcoal"
        } hover:text-brass`}
        aria-label="Account"
        data-testid="user-menu-button"
      >
        <UserCircle size={22} strokeWidth={1.3} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-3 w-64 bg-ivory shadow-lg border border-mist"
          data-testid="user-menu-dropdown"
          role="menu"
        >
          <div className="px-5 pt-5 pb-3">
            <p className="eyebrow text-charcoal/60" style={{ fontSize: 10 }}>
              Signed in as
            </p>
            <p
              className="font-display text-charcoal mt-1 text-base truncate"
              data-testid="user-menu-email"
            >
              {user.email}
            </p>
            <p className="eyebrow text-brass mt-1.5" style={{ fontSize: 10 }}>
              {user.role}
              {user.show_prices ? " · with prices" : ""}
            </p>
          </div>
          <span aria-hidden className="block h-px bg-mist mx-5" />
          {isAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-3 px-5 py-3 text-sm text-charcoal hover:bg-pearl/50"
              onClick={() => setOpen(false)}
              data-testid="user-menu-admin"
            >
              <Shield size={15} strokeWidth={1.4} />
              <span>Admin panel</span>
            </Link>
          )}
          <button
            type="button"
            onClick={async () => {
              setOpen(false);
              await logout();
            }}
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-charcoal hover:bg-pearl/50 border-t border-mist"
            data-testid="user-menu-signout"
          >
            <LogOut size={15} strokeWidth={1.4} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
